import React, { useCallback, useMemo } from 'react'
import { Contract, NFTCategory, Network } from '@dcl/schemas'
import { withAuthorizedAction } from 'decentraland-dapps/dist/containers'
import { AuthorizedAction } from 'decentraland-dapps/dist/containers/withAuthorizedAction/AuthorizationModal'
import { getChainIdByNetwork } from 'decentraland-dapps/dist/lib'
import { AuthorizationType } from 'decentraland-dapps/dist/modules/authorization'
import { ContractName, getContract as getDCLContract } from 'decentraland-transactions'
import { config } from '../../../../config'
import { getClaimNameStatus, getErrorMessage } from '../../../../modules/ens/selectors'
import { PRICE_IN_WEI } from '../../../../modules/ens/utils'
import { NFT } from '../../../../modules/nft/types'
import { getContractNames, VendorName } from '../../../../modules/vendor'
import { Contract as DCLContract } from '../../../../modules/vendor/services'
import BuyWithCryptoModal from '../BuyWithCryptoModal.container'
import { OnGetCrossChainRoute, OnGetGasCost } from '../BuyWithCryptoModal.types'
import { useCrossChainNameMintingRoute, useNameMintingGasCost } from '../hooks'
import { Props } from './MintNameWithCryptoModal.types'

export const CONTROLLER_V2_ADDRESS = config.get('CONTROLLER_V2_CONTRACT_ADDRESS', '')

const MintNameWithCryptoModalHOC = (props: Props) => {
  const {
    name: modalName,
    isMintingName,
    isMintingNameCrossChain,
    metadata: { name, useCredits },
    isUsingMagic,
    isLoadingAuthorization,
    credits,
    getContract,
    onAuthorizedAction,
    onClaimName,
    onClaimNameCrossChain,
    onClaimNameWithCredits,
    onOpenFatFingerModal,
    onCloseFatFingerModal,
    onCloseAuthorization,
    onClose
  } = props

  const onBuyNatively = useCallback(() => {
    const contractNames = getContractNames()

    const mana = getContract({
      name: contractNames.MANA,
      network: Network.ETHEREUM
    }) as DCLContract

    const manaContract: Contract = {
      name: ContractName.MANAToken,
      address: mana.address,
      chainId: getChainIdByNetwork(Network.ETHEREUM),
      network: Network.ETHEREUM,
      category: NFTCategory.ENS
    }

    onAuthorizedAction({
      // Override the automatic Magic sign in as the user will need to pay gas for the transaction
      manual: true,
      authorizedAddress: CONTROLLER_V2_ADDRESS,
      authorizedContractLabel: 'DCLControllerV2',
      targetContract: manaContract,
      targetContractName: ContractName.MANAToken,
      requiredAllowanceInWei: PRICE_IN_WEI,
      authorizationType: AuthorizationType.ALLOWANCE,
      onAuthorized: () => onClaimName(name)
    })
  }, [name, getContract, onAuthorizedAction, onClaimName])

  const onGetCrossChainRoute: OnGetCrossChainRoute = useCallback(
    (selectedToken, selectedChain, providerTokens, crossChainProvider, wallet, withCredits = false) =>
      useCrossChainNameMintingRoute(
        name,
        PRICE_IN_WEI,
        getChainIdByNetwork(Network.ETHEREUM),
        selectedToken,
        selectedChain,
        providerTokens,
        crossChainProvider,
        wallet,
        withCredits
      ),
    [name]
  )

  const onGetGasCost: OnGetGasCost = useCallback(
    (selectedToken, chainNativeToken, wallet) => useNameMintingGasCost(name, selectedToken, chainNativeToken, wallet),
    [name]
  )

  const onGoBack = useCallback(() => {
    onClose()
    onOpenFatFingerModal()
  }, [onOpenFatFingerModal, onClose])

  const onCloseModal = useCallback(() => {
    onCloseFatFingerModal()
    return onClose()
  }, [onClose])

  const onBuyWithCredits = useCallback(
    (manaToSpendByUser: bigint) => {
      if (manaToSpendByUser === 0n) {
        onClaimNameWithCredits()
        return
      }
      let creditsManager
      try {
        creditsManager = getDCLContract(ContractName.CreditsManager, getChainIdByNetwork(Network.MATIC))
      } catch (error) {
        console.log('Error getting credit manager', error)
        return
      }
      const contractNames = getContractNames()
      const manaContract = getContract({
        name: contractNames.MANA,
        network: Network.MATIC
      }) as DCLContract

      onAuthorizedAction({
        // Override the automatic Magic sign in as the user will need to pay gas for the transaction
        manual: true,
        authorizedAddress: creditsManager.address,
        authorizedContractLabel: creditsManager.name,
        targetContract: manaContract as Contract,
        targetContractName: ContractName.MANAToken,
        requiredAllowanceInWei: manaToSpendByUser.toString(),
        authorizationType: AuthorizationType.ALLOWANCE,
        onAuthorized: () => {
          onCloseAuthorization()
          onClaimNameWithCredits()
        }
      })
    },
    [onClaimNameWithCredits]
  )

  // Emulates a NFT for the item to be minted so it can be shown in the modal
  const asset: NFT = useMemo(
    () => ({
      chainId: useCredits ? getChainIdByNetwork(Network.MATIC) : getChainIdByNetwork(Network.ETHEREUM),
      network: useCredits ? Network.MATIC : Network.ETHEREUM,
      name,
      data: {
        ens: { subdomain: name }
      },
      category: NFTCategory.ENS,
      updatedAt: Date.now(),
      id: '1',
      itemId: '1',
      tokenId: '1',
      owner: '0x0',
      vendor: VendorName.DECENTRALAND,
      activeOrderId: null,
      openRentalId: null,
      image: '',
      url: '',
      issuedId: '1',
      createdAt: Date.now(),
      soldAt: Date.now(),
      price: PRICE_IN_WEI,
      contractAddress: CONTROLLER_V2_ADDRESS
    }),
    [name]
  )

  const price = useMemo(() => {
    if (!useCredits || !credits) return PRICE_IN_WEI
    const adjustedPrice = BigInt(PRICE_IN_WEI) - BigInt(credits.totalCredits)
    // Convert back to wei format
    return adjustedPrice < 0 ? '0' : adjustedPrice.toString()
  }, [PRICE_IN_WEI, useCredits, credits])

  return (
    <BuyWithCryptoModal
      price={price}
      isBuyingAsset={isMintingName || isMintingNameCrossChain}
      onBuyNatively={onBuyNatively}
      onBuyCrossChain={onClaimNameCrossChain}
      onBuyWithCredits={onBuyWithCredits}
      onGetGasCost={onGetGasCost}
      isLoadingAuthorization={isLoadingAuthorization}
      isUsingMagic={isUsingMagic}
      onGetCrossChainRoute={onGetCrossChainRoute}
      metadata={{ asset }}
      useCredits={useCredits}
      name={modalName}
      onGoBack={onGoBack}
      onClose={onCloseModal}
    />
  )
}

export const MintNameWithCryptoModal = React.memo(
  withAuthorizedAction(
    MintNameWithCryptoModalHOC,
    AuthorizedAction.CLAIM_NAME,
    {
      action: 'names_page.claim_name_fat_finger_modal.authorization.action',
      title_action: 'names_page.claim_name_fat_finger_modal.authorization.title_action'
    },
    getClaimNameStatus,
    getErrorMessage
  )
)
